import { useCallback, useEffect, useRef, useState, type FC } from "react";
import styled from "styled-components";

import { useVisibility } from "@ds/hooks/use-visibility";
import { usePagination, useRootDispatch, useRootSelector, useTableDataSorting } from "@ds/hooks";

import { isAggregatedEntity } from "@ds/model/entity-model";

import { CreateCampaignItemModal } from "@ds/modules/content/campaign-items/components/forms/create-campaign-item-modal";
import { UpdateCampaignItemModal } from "@ds/modules/content/campaign-items/components/forms/update-campaign-item-modal";
import {
  selectCampaignItemsIsLoading,
  selectSelectedCampaignItems,
  selectTableCampaignItems,
} from "@ds/modules/content/campaign-items/redux/selectors";
import { campaignItemsActions, CampaignItemsRemoteOperation } from "@ds/modules/content/campaign-items/redux/slice";
import { DEFAULT_TABLE_TAB } from "@ds/modules/table-data/redux/slice";
import { DetailsTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { DetailsCard } from "@ds/components/details-view";
import { MultipleValuesLabel } from "@ds/components/forms/labels/common";
import { Loading } from "@ds/components/loaders/loading";
import { Tooltip } from "@ds/components/tooltips";
import { DataTableRowReorderEvent } from "@ds/components/table/data-table";
import { DetailsTable } from "@ds/components/table/details-table";
import { Column, getReorderColumn, getSelectionColumn } from "@ds/components/table/table-columns";
import { TotalSelectedLabel } from "@ds/components/table/total-selected-label";
import { stringToHSLCSS } from "@ds/utils/color";
import { composeRoute } from "@ds/utils/router";
import { CONTENT_PLAYLIST_DETAILS_ROUTE } from "@ds/constants/router/entity-routes";
import { TextLink } from "@ds/components/text-link";

import { CampaignDetailsItemsCardActionsMenu } from "./campaign-details-items-card-actions-menu";
import { CampaignItemWeightVisualization } from "./campaign-item-weight-visualization";

const HeaderIcon = styled.i`
  margin-top: 3px;
`;

const LegendMarker = styled.div`
  border-radius: 9999px;
  height: 24px;
  width: 24px;
`;

const getLegendBodyTemplate = (item: CampaignItem) => (
  <LegendMarker
    className="campaign-item-legend-cell"
    style={{ backgroundColor: stringToHSLCSS(item.playlist_name ?? " ") }}
  />
);

const getPlaylistNameBodyTemplate = (item: CampaignItem) =>
  item.playlist_name ? (
    <TextLink to={composeRoute(CONTENT_PLAYLIST_DETAILS_ROUTE, { playlistId: item.playlist_id })}>
      {item.playlist_name}
    </TextLink>
  ) : (
    ""
  );

const getNumAssetsBodyTemplate = (item: CampaignItem) => item.playlist_items_count;

const getWeightBodyTemplate = (item: CampaignItem) => (item.weight !== undefined ? `${item.weight}%` : "");

const getRandomizeBodyTemplate = (item: CampaignItem) => (item.settings?.randomize ? "Yes" : "-");

const getMinGapBodyTemplate = (item: CampaignItem) => (
  <span className="campaign-item-min-gap-cell">{item.settings?.min_gap || 0}</span>
);

const getMaxConsecutiveBodyTemplate = (item: CampaignItem) => (
  <span className="campaign-item-max-consecutive-cell">{item.settings?.max_consecutive || 0}</span>
);

export const CampaignDetailsItemsCard: FC<{ campaign: Campaign }> = ({ campaign }) => {
  const isAggregated = isAggregatedEntity(campaign);
  const showInterleavedColumns = campaign.mixing_strategy !== "time_slice";

  const [isCreateModalVisible, setIsCreateModalVisible] = useVisibility();
  const [editTargetItems, setEditTargetItems] = useState<CampaignItem[] | undefined>(undefined);

  const selectedEntities = useRootSelector(selectSelectedCampaignItems);
  const entities = useRootSelector(selectTableCampaignItems);

  const isLoading = useRootSelector(selectCampaignItemsIsLoading);
  const checkboxIsLoading = useRootSelector(state =>
    selectCampaignItemsIsLoading(state, CampaignItemsRemoteOperation.CHECKBOXES),
  );

  // Only advance the chart's items when loading is complete AND the underlying
  // data has actually changed. Selection changes produce a new `entities` array
  // reference with identical content (due to selector recomputation), which would
  // otherwise trigger unnecessary chart re-renders and animations.
  const chartDataKey = entities
    .map(
      e =>
        `${e.id}:${e.weight ?? 0}:${e.campaign_order}:${e.playlist_name ?? ""}:${e.settings?.min_gap ?? 0}:${e.settings?.max_consecutive ?? 0}:${e.settings?.randomize ? 1 : 0}`,
    )
    .join(",");
  const chartEntitiesRef = useRef<CampaignItem[]>(entities);
  const chartDataKeyRef = useRef<string>(chartDataKey);
  if (!isLoading && chartDataKey !== chartDataKeyRef.current) {
    chartEntitiesRef.current = entities;
    chartDataKeyRef.current = chartDataKey;
  }

  const [pagination] = usePagination(
    DetailsTableDataTypeEnum.ContentCampaignItems,
    isAggregated ? DEFAULT_TABLE_TAB : campaign.id.toString(),
    { campaignId: campaign.id },
  );

  const [{ sortOrder, sortField }] = useTableDataSorting(
    DetailsTableDataTypeEnum.ContentCampaignItems,
    isAggregated ? DEFAULT_TABLE_TAB : campaign.id.toString(),
    { campaignId: campaign.id },
  );

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (!isAggregated) {
      dispatch(
        campaignItemsActions.fetchCampaignItems(
          { campaignId: campaign.id },
          { tableType: DetailsTableDataTypeEnum.ContentCampaignItems, cache: { disableCache: true } },
        ),
      );
    }
  }, [dispatch, isAggregated, campaign.id]);

  const onReorderHandler = useCallback(
    (currIndex: number, newIndex: number) => {
      if (currIndex !== newIndex) {
        dispatch(campaignItemsActions.reorderCampaignItems({ currIndex, newIndex }, { campaignId: campaign.id }));
      }
    },
    [dispatch, campaign.id],
  );

  const onTableRowReorderHandler = useCallback(
    (e: DataTableRowReorderEvent<CampaignItem[]>) => onReorderHandler(e.dragIndex, e.dropIndex),
    [onReorderHandler],
  );

  const onToggleRowSelected = useCallback(
    (entry: CampaignItem) => dispatch(campaignItemsActions.toggleSelected(entry.id)),
    [dispatch],
  );

  const onSingleRowSelectHandler = useCallback(
    (entry: CampaignItem) => dispatch(campaignItemsActions.selectCampaignItems([entry])),
    [dispatch],
  );

  const onToggleAllHandler = useCallback(
    (isSelectedAll: boolean) => {
      if (isSelectedAll) {
        dispatch(
          campaignItemsActions.fetchCampaignItems(
            { campaignId: campaign.id },
            undefined,
            CampaignItemsRemoteOperation.CHECKBOXES,
          ),
        );
      } else {
        dispatch(campaignItemsActions.selectCampaignItems([]));
      }
    },
    [dispatch, campaign.id],
  );

  return (
    <>
      {showInterleavedColumns && (
        <>
          <Tooltip target=".campaign-item-randomize-header" content="Randomized" position="top" />
          <Tooltip target=".campaign-item-randomize-cell" content="Randomized" position="top" />
          <Tooltip target=".campaign-item-min-gap-header" content="Minimum Gap" position="top" />
          <Tooltip target=".campaign-item-min-gap-cell" content="Minimum Gap" position="top" />
          <Tooltip target=".campaign-item-max-consecutive-header" content="Max Consecutive" position="top" />
          <Tooltip target=".campaign-item-max-consecutive-cell" content="Max Consecutive" position="top" />
        </>
      )}

      <DetailsCard
        title="Playlists"
        icon="list"
        actions={
          !isAggregated && (
            <CampaignDetailsItemsCardActionsMenu
              campaign={campaign}
              selectedEntities={selectedEntities}
              onCreate={setIsCreateModalVisible(true)}
              onUpdate={() => setEditTargetItems(selectedEntities)}
              onReorder={onReorderHandler}
            />
          )
        }
      >
        <Loading isLoading={isLoading}>
          {!isAggregated && (
            <>
              <CampaignItemWeightVisualization
                items={chartEntitiesRef.current}
                mixingStrategy={campaign.mixing_strategy}
              />

              <hr />

              <DetailsTable
                value={entities}
                selection={selectedEntities}
                onSingleRowSelect={onSingleRowSelectHandler}
                toggleRowSelected={onToggleRowSelected}
                loading={isLoading}
                sortField={sortField}
                sortOrder={sortOrder}
                onRowReorder={onTableRowReorderHandler}
              >
                {getReorderColumn({ rowReorder: selectedEntities.length < 2 })}
                {getSelectionColumn({
                  total: pagination.total,
                  selected: selectedEntities.length,
                  isLoading: checkboxIsLoading,
                  onToggleAll: onToggleAllHandler,
                })}

                <Column
                  header=""
                  body={getLegendBodyTemplate}
                  resizeable={false}
                  style={{ flex: "none", paddingTop: "18px", width: "40px" }}
                />
                <Column
                  header="Playlist"
                  body={getPlaylistNameBodyTemplate}
                  style={{ flexGrow: 3 }}
                  resizeable={false}
                />
                <Column
                  field="weight"
                  header="Weight"
                  body={getWeightBodyTemplate}
                  className="center"
                  resizeable={false}
                  style={{ flex: "none", width: "100px" }}
                />
                <Column
                  header="# Assets"
                  body={getNumAssetsBodyTemplate}
                  className="center"
                  resizeable={false}
                  style={{ flex: "none", width: "100px" }}
                />

                {showInterleavedColumns && (
                  <Column
                    header="Randomized"
                    body={getRandomizeBodyTemplate}
                    className="center"
                    style={{ flex: "none", width: "120px" }}
                    resizeable={false}
                  />
                )}
                {showInterleavedColumns && (
                  <Column
                    header={<HeaderIcon className="pi pi-arrows-h campaign-item-min-gap-header" />}
                    body={getMinGapBodyTemplate}
                    className="center"
                    style={{ flex: "none", width: "100px" }}
                    resizeable={false}
                  />
                )}
                {showInterleavedColumns && (
                  <Column
                    header={<HeaderIcon className="pi pi-angle-double-right campaign-item-max-consecutive-header" />}
                    body={getMaxConsecutiveBodyTemplate}
                    className="center"
                    style={{ flex: "none", width: "100px" }}
                    resizeable={false}
                  />
                )}
              </DetailsTable>

              <TotalSelectedLabel
                selectedCount={checkboxIsLoading ? Number(pagination.total) : selectedEntities.length}
              />
            </>
          )}

          {isAggregated && <MultipleValuesLabel />}
        </Loading>
      </DetailsCard>

      {isCreateModalVisible && (
        <CreateCampaignItemModal campaign={campaign} allItems={entities} onClose={setIsCreateModalVisible(false)} />
      )}

      {editTargetItems && editTargetItems.length > 0 && (
        <UpdateCampaignItemModal
          campaignItems={editTargetItems}
          campaign={campaign}
          allItems={entities}
          onClose={() => setEditTargetItems(undefined)}
        />
      )}
    </>
  );
};
